/**
 * RevenueCat SDK Configuration
 * API Key: configured via env
 * Entitlement: RepasoGPC
 */

import type { Package, Purchases } from '@revenuecat/purchases-js';

// RevenueCat Web Billing public API key (safe to expose in frontend).
// Production keys start with `rcb_`; sandbox keys start with `rcb_sb_`.
// App ID: app95c9f3af54 (RepasoGPC - Live)
const REVENUECAT_API_KEY =
    (import.meta.env.VITE_REVENUECAT_API_KEY as string | undefined) ||
    'rcb_HpOhEXhhCtHUEOFisxvkSSlrgCjg';

// Entitlement identifier for premium access
export const ENTITLEMENT_ID = 'RepasoGPC';

// Product identifiers
export const PRODUCT_IDS = {
    MONTHLY: 'monthly',
    YEARLY: 'yearly',
} as const;

// Singleton instance
let purchasesInstance: Purchases | null = null;

/**
 * Initialize RevenueCat with a user ID
 * Should be called after user authentication
 */
export async function initializeRevenueCat(userId: string): Promise<Purchases> {
    try {
        const { Purchases } = await import('@revenuecat/purchases-js');
        if (!REVENUECAT_API_KEY) {
            throw new Error('RevenueCat API key is not configured');
        }

        if (!purchasesInstance) {
            purchasesInstance = Purchases.isConfigured()
                ? Purchases.getSharedInstance()
                : Purchases.configure({ apiKey: REVENUECAT_API_KEY, appUserId: userId });
        }

        if (purchasesInstance.getAppUserId() !== userId) {
            if (purchasesInstance.isAnonymous()) {
                await purchasesInstance.identifyUser(userId);
            } else {
                await purchasesInstance.changeUser(userId);
            }
        }

        return purchasesInstance;
    } catch (error) {
        console.error('[RevenueCat] Failed to initialize:', error);
        throw error;
    }
}

export async function fetchPublicOfferings(): Promise<Package[]> {
    if (!REVENUECAT_API_KEY) return [];

    const { Purchases } = await import('@revenuecat/purchases-js');
    if (!purchasesInstance) {
        purchasesInstance = Purchases.isConfigured()
            ? Purchases.getSharedInstance()
            : Purchases.configure({
                apiKey: REVENUECAT_API_KEY,
                appUserId: Purchases.generateRevenueCatAnonymousAppUserId(),
            });
    }

    const offerings = await purchasesInstance.getOfferings();
    return offerings.current?.availablePackages ?? [];
}

/**
 * Get the current RevenueCat instance
 * Throws if not initialized
 */
export function getRevenueCat(): Purchases {
    if (!purchasesInstance) {
        throw new Error('RevenueCat not initialized. Call initializeRevenueCat first.');
    }
    return purchasesInstance;
}

/**
 * Check if RevenueCat is initialized
 */
export function isRevenueCatInitialized(): boolean {
    return purchasesInstance !== null;
}

/**
 * Reset RevenueCat instance (for logout)
 */
export function resetRevenueCat(): void {
    // Keep the SDK singleton alive. The next authenticated session switches
    // identity through initializeRevenueCat, while public pages may still read offerings.
}
